import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

export default function MakePayment() {
    const location = useLocation();
    const navigate = useNavigate();
    const [card, setCard] = useState(null);
    const [payments, setPayments] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const cardContainerRef = useRef(null);

    const applicationId = "sandbox-sq0idb-ciK-AXiZD_0vLxk2-blcwA";
    const locationId = "LDF3D892Z9CYF";

    useEffect(() => {
        if (!location.state?.paymentData) {
            toast.error("No payment information found");
            navigate('/make-payment');
            return;
        }

        console.log("Payment Data:", location.state.paymentData);
        console.log("Form Data:", location.state.formData);

        const initializeSquarePaymentForm = async () => {
            if (!window.Square) {
                console.error("Square SDK not available.");
                return;
            }

            try {
                const paymentsInstance = window.Square.payments(applicationId, locationId);
                setPayments(paymentsInstance);
                const cardInstance = await paymentsInstance.card();
                
                if (cardContainerRef.current) {
                    cardContainerRef.current.innerHTML = '';
                }

                await cardInstance.attach('#card-container');
                setCard(cardInstance);
            } catch (error) {
                console.error("Error initializing Square Payments:", error);
                toast.error("Failed to initialize payment system");
            }
        };

        initializeSquarePaymentForm();

        return () => {
            if (cardContainerRef.current) {
                cardContainerRef.current.innerHTML = '';
            }
        };
    }, [location.state?.paymentData, navigate]);

    const handleSubmitPayment = async () => {
        if (!card || !payments) {
            toast.error("Payment system not ready yet");
            return;
        }

        setIsProcessing(true);

        try {
            const tokenResult = await card.tokenize();
            
            if (tokenResult.status !== "OK") {
                console.error("Tokenization failed:", tokenResult.errors);
                toast.error("Please check your card details and try again.");
                return;
            }

            const token = tokenResult.token;

            // Log the payment data we received
            console.log("Full payment data:", location.state.paymentData);
            console.log("UUID from payment data:", location.state.paymentData.uuid);
            console.log("Form data:", location.state.formData);

            // Ensure currency is in correct format
            let currencyCode = location.state.formData.currency_code;
            if (currencyCode === "Euro") {
                currencyCode = "EUR";
            }
            console.log("Using currency code:", currencyCode);

            // Proceed with payment
            const proceedPayload = {
                token: token,
                uuid: location.state.paymentData.uuid,
                amountMoney: {
                    currency: currencyCode,
                    amount: location.state.formData.amount
                }
            };

            console.log("Sending proceed payload:", proceedPayload);

            const proceedResponse = await axios.post(`${process.env.REACT_APP_URL}/payment/proceed`, proceedPayload);
            console.log("Proceed payment response:", proceedResponse.data);

            toast.success("Payment Processed Successfully!");
            setPaymentSuccess(true);
        } catch (error) {
            console.error("Full error object:", error);
            console.error("Error response:", error.response);
            console.error("Error message:", error.message);
            console.error("Error details:", error.response?.data);
            
            if (error.response) {
                toast.error(error.response.data?.message || error.response.data || "Server error occurred");
            } else if (error.request) {
                toast.error("No response from server. Please check your connection.");
            } else {
                toast.error("Error processing payment. Please try again.");
            }
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="container p-5" style={{ maxWidth: 700 }}>
            <div style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)', borderRadius: 16, background: '#fff', padding: 32 }}>
                <h3 style={{ color: '#2196f3', fontWeight: 600, marginBottom: 24 }}>Payment Details</h3>
                {location.state?.formData && (
                    <table style={{ width: '100%', marginBottom: 24, fontSize: 16 }}>
                        <tbody>
                            <tr>
                                <td style={{ fontWeight: 500, padding: '8px 0', width: 120 }}>Name:</td>
                                <td>{location.state.formData.name_title} {location.state.formData.name}</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 500, padding: '8px 0' }}>Email:</td>
                                <td>{location.state.formData.email}</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 500, padding: '8px 0' }}>Amount:</td>
                                <td style={{ color: '#388e3c', fontWeight: 600 }}>{location.state.formData.amount} {location.state.formData.currency_code}</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 500, padding: '8px 0' }}>Journal:</td>
                                <td>{location.state.formData.journal_name}</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 500, padding: '8px 0' }}>Article:</td>
                                <td>{location.state.formData.article_title}</td>
                            </tr>
                        </tbody>
                    </table>
                )}
                <div style={{ marginTop: 32, marginBottom: 16 }}>
                    <h4 style={{ color: '#2196f3', fontWeight: 500, marginBottom: 16 }}>Enter Card Details</h4>
                    <div style={{ boxShadow: '0 2px 8px rgba(33,150,243,0.08)', border: '1px solid #e3eaf1', borderRadius: 10, padding: 20, background: '#f8fafc', marginBottom: 24 }}>
                        {/* Only the Square card input should be rendered below. Do not add any other card fields here. */}
                        {!paymentSuccess && (
                            <div id="card-container" ref={cardContainerRef} style={{
                                marginBottom: '1rem',
                                border: '1px solid #b6c6d6',
                                borderRadius: '6px',
                                padding: '10px',
                                background: '#fff'
                            }}></div>
                        )}
                        <button 
                            className="btn btn-primary" 
                            onClick={handleSubmitPayment}
                            disabled={isProcessing || paymentSuccess}
                            style={{ width: '100%', padding: '12px 0', fontSize: 18, borderRadius: 8, fontWeight: 600, background: '#2196f3', border: 'none', boxShadow: '0 2px 8px rgba(33,150,243,0.08)' }}
                        >
                            {isProcessing ? "Processing..." : "Submit Payment"}
                        </button>
                    </div>
                </div>
            </div>
            {/* Payment Success Modal */}
            {paymentSuccess && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 16,
                        padding: '40px 32px',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                        textAlign: 'center',
                        minWidth: 320
                    }}>
                        <div style={{ fontSize: 60, color: '#4caf50', marginBottom: 16 }}>
                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="12" fill="#4caf50"/><path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 600, color: '#388e3c', marginBottom: 12 }}>Payment Successful</div>
                        <button onClick={() => navigate('/')} style={{
                            marginTop: 16,
                            padding: '10px 32px',
                            fontSize: 18,
                            borderRadius: 8,
                            background: '#2196f3',
                            color: '#fff',
                            border: 'none',
                            fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(33,150,243,0.08)'
                        }}>Back to Home</button>
                    </div>
                </div>
            )}
        </div>
    );
}
